class MaatiPdp extends HTMLElement {
  connectedCallback() {
    this.variantInput = this.querySelector('[data-maati-variant-input]');
    this.quantityInput = this.querySelector('[data-maati-quantity-input]');
    this.quantityDisplays = Array.from(this.querySelectorAll('[data-maati-quantity-value]'));
    this.priceCurrent = Array.from(this.querySelectorAll('[data-maati-price-current]'));
    this.stickyPrice = this.querySelector('[data-maati-sticky-price]');
    this.priceCompare = this.querySelector('[data-maati-price-compare]');
    this.addButton = this.querySelector('[data-maati-add]');
    this.submitButtons = Array.from(this.querySelectorAll('[data-maati-submit-button]'));
    this.heroImage = this.querySelector('[data-maati-gallery-image]');
    this.heroSource = this.querySelector('[data-maati-gallery-source]');
    this.heroAlt = this.querySelector('[data-maati-gallery-alt]');
    this.galleryThumbs = Array.from(this.querySelectorAll('[data-maati-thumb]'));
    this.galleryDialog = this.querySelector('[data-maati-gallery-dialog]');
    this.galleryDialogImage = this.querySelector('[data-maati-gallery-dialog-image]');
    this.productForm = this.querySelector('[data-product-url]');
    this.galleryAutoplayDelay = 2000;
    this.galleryResumeDelay = 6000;
    this.galleryAutoplayTimer = null;
    this.galleryResumeTimer = null;
    this.activeUnitPrice = Number(this.stickyPrice?.dataset.unitPrice || 0);

    const selectedVariantCard = this.querySelector('[data-maati-variant-card][aria-pressed="true"]');
    if (selectedVariantCard?.dataset.mediaUrl) {
      this.activateThumbForMedia(selectedVariantCard.dataset.mediaUrl);
    }

    this.addEventListener('click', (event) => {
      const variantCard = event.target.closest('[data-maati-variant-card]');
      if (variantCard) {
        this.selectVariant(variantCard);
        return;
      }

      const thumb = event.target.closest('[data-maati-thumb]');
      if (thumb) {
        this.pauseGalleryAutoplay();
        this.selectMedia(thumb);
        return;
      }

      if (event.target.closest('[data-maati-gallery-prev]')) {
        this.pauseGalleryAutoplay();
        this.navigateGallery(-1);
        return;
      }

      if (event.target.closest('[data-maati-gallery-next]')) {
        this.pauseGalleryAutoplay();
        this.navigateGallery(1);
        return;
      }

      if (event.target.closest('[data-maati-zoom-open]')) {
        this.pauseGalleryAutoplay(false);
        this.openGalleryDialog();
        return;
      }

      if (event.target.closest('[data-maati-zoom-close]')) {
        this.closeGalleryDialog();
        this.startGalleryAutoplay();
        return;
      }

      const quantityButton = event.target.closest('[data-maati-quantity]');
      if (quantityButton) {
        event.preventDefault();
        this.changeQuantity(quantityButton.dataset.maatiQuantity);
      }
    });

    this.quantityInput?.addEventListener('input', () => this.syncQuantityState());
    this.quantityInput?.addEventListener('change', () => this.syncQuantityState());
    this.updateQuantityDisplays();
    this.updateStickyPrice();
    this.galleryDialog?.addEventListener('close', () => this.startGalleryAutoplay());
    this.querySelectorAll('[data-maati-buy-now]').forEach((button) => {
      button.addEventListener('click', (event) => {
        this.submitBuyNow(event, button);
      });
    });
    this.startGalleryAutoplay();
  }

  disconnectedCallback() {
    this.stopGalleryAutoplay();
  }

  submitBuyNow(event, button) {
    const form = button.closest('form');
    if (!form || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const returnToInput = form.querySelector('input[name="return_to"]') || document.createElement('input');
    returnToInput.type = 'hidden';
    returnToInput.name = 'return_to';
    returnToInput.value = '/checkout';
    if (!returnToInput.parentElement) form.append(returnToInput);

    const submitterInput = form.querySelector('input[name="add"][data-maati-buy-now-submit]') || document.createElement('input');
    submitterInput.type = 'hidden';
    submitterInput.name = 'add';
    submitterInput.value = '';
    submitterInput.setAttribute('data-maati-buy-now-submit', '');
    if (!submitterInput.parentElement) form.append(submitterInput);

    HTMLFormElement.prototype.submit.call(form);
  }

  selectVariant(card) {
    if (card.disabled || !this.variantInput) return;

    this.querySelectorAll('[data-maati-variant-card]').forEach((item) => {
      item.setAttribute('aria-pressed', String(item === card));
    });

    this.variantInput.value = card.dataset.variantId;
    this.updateVariantUrl(card.dataset.variantId);

    if (this.priceCurrent.length) {
      this.priceCurrent.forEach((priceNode) => {
        priceNode.textContent = card.dataset.priceText || '';
      });
    }

    this.activeUnitPrice = Number(card.dataset.price || this.activeUnitPrice || 0);
    this.updateStickyPrice();

    if (this.priceCompare) {
      this.priceCompare.textContent = card.dataset.compareText || '';
      this.priceCompare.hidden = !card.dataset.compareText;
    }

    const available = card.dataset.available === 'true';
    if (this.submitButtons.length) {
      this.submitButtons.forEach((button) => {
        button.disabled = !available;
      });
    }

    const addLabels = this.querySelectorAll('[data-maati-add-label]');
    if (addLabels.length) {
      const fallbackSoldOutText = this.addButton?.dataset.soldOutText || card.dataset.variantTitle || '';
      addLabels.forEach((labelNode) => {
        const parentButton = labelNode.closest('[data-maati-submit-button]');
        const label = available
          ? parentButton?.dataset.availableText || this.addButton?.dataset.availableText || labelNode.textContent
          : parentButton?.dataset.soldOutText || fallbackSoldOutText;
        if (label) labelNode.textContent = label;
      });
    }

    const cartLabel = this.querySelector('[data-maati-cart-label]');
    if (cartLabel && this.addButton) {
      cartLabel.textContent = available ? cartLabel.dataset.availableText || cartLabel.textContent : this.addButton.dataset.soldOutText;
    }

    if (card.dataset.mediaUrl) {
      this.setHeroImage(card.dataset.mediaUrl, card.dataset.mediaAlt || card.dataset.variantTitle || '');
      this.activateThumbForMedia(card.dataset.mediaUrl);
    }
  }

  selectMedia(button) {
    this.galleryThumbs.forEach((item) => {
      item.setAttribute('aria-current', String(item === button));
    });

    this.setHeroImage(button.dataset.mediaUrl, button.dataset.mediaAlt || '');
  }

  setHeroImage(url, alt) {
    if (!url || !this.heroImage) return;

    if (this.heroSource) {
      this.heroSource.setAttribute('srcset', url);
    }

    this.heroImage.src = url;
    this.heroImage.removeAttribute('srcset');
    this.heroImage.alt = alt;

    if (this.heroAlt) {
      this.heroAlt.textContent = alt;
    }

    if (this.galleryDialogImage) {
      this.galleryDialogImage.src = url;
      this.galleryDialogImage.alt = alt;
    }
  }

  activateThumbForMedia(url) {
    if (!url) return;

    this.galleryThumbs.forEach((item) => {
      item.setAttribute('aria-current', String(item.dataset.mediaUrl === url));
    });
  }

  navigateGallery(direction) {
    if (!this.galleryThumbs.length) return;

    const currentIndex = Math.max(
      0,
      this.galleryThumbs.findIndex((item) => item.getAttribute('aria-current') === 'true')
    );
    const nextIndex = (currentIndex + direction + this.galleryThumbs.length) % this.galleryThumbs.length;
    this.selectMedia(this.galleryThumbs[nextIndex]);
  }

  startGalleryAutoplay() {
    this.stopGalleryAutoplay();
    if (this.galleryThumbs.length < 2 || this.galleryDialog?.open) return;

    this.galleryAutoplayTimer = window.setInterval(() => {
      if (!this.galleryDialog?.open) {
        this.navigateGallery(1);
      }
    }, this.galleryAutoplayDelay);
  }

  stopGalleryAutoplay() {
    if (this.galleryAutoplayTimer) {
      window.clearInterval(this.galleryAutoplayTimer);
      this.galleryAutoplayTimer = null;
    }
    if (this.galleryResumeTimer) {
      window.clearTimeout(this.galleryResumeTimer);
      this.galleryResumeTimer = null;
    }
  }

  pauseGalleryAutoplay(shouldResume = true) {
    this.stopGalleryAutoplay();
    if (!shouldResume) return;

    this.galleryResumeTimer = window.setTimeout(() => {
      this.startGalleryAutoplay();
    }, this.galleryResumeDelay);
  }

  openGalleryDialog() {
    if (!this.galleryDialog) return;

    if (typeof this.galleryDialog.showModal === 'function') {
      this.galleryDialog.showModal();
    } else {
      this.galleryDialog.setAttribute('open', '');
    }
  }

  closeGalleryDialog() {
    if (!this.galleryDialog) return;

    if (typeof this.galleryDialog.close === 'function') {
      this.galleryDialog.close();
    } else {
      this.galleryDialog.removeAttribute('open');
    }
  }

  updateVariantUrl(variantId) {
    if (!variantId || !window.history?.replaceState) return;

    const nextUrl = new URL(window.location.href);
    const productPath = this.productForm?.dataset.productUrl;

    if (productPath) {
      nextUrl.pathname = productPath;
    }

    nextUrl.searchParams.set('variant', variantId);
    window.history.replaceState({ path: nextUrl.toString() }, '', nextUrl.toString());
  }

  changeQuantity(direction) {
    if (!this.quantityInput) return;

    const min = Number(this.quantityInput.min || 1);
    const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
    const step = Number(this.quantityInput.step || 1);
    const current = Number(this.quantityInput.value || min);
    const next = direction === 'increase' ? current + step : current - step;

    this.quantityInput.value = Math.min(max, Math.max(min, next));
    this.updateQuantityDisplays();
    this.updateStickyPrice();
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateQuantityDisplays() {
    if (!this.quantityInput || !this.quantityDisplays.length) return;

    this.quantityDisplays.forEach((display) => {
      display.textContent = this.quantityInput.value || this.quantityInput.min || '1';
    });
  }

  syncQuantityState() {
    this.updateQuantityDisplays();
    this.updateStickyPrice();
  }

  updateStickyPrice() {
    if (!this.stickyPrice || !this.activeUnitPrice || !this.quantityInput) return;

    const quantity = Number(this.quantityInput.value || this.quantityInput.min || 1);
    const total = this.activeUnitPrice * quantity;
    this.stickyPrice.textContent = this.formatRupees(total);
  }

  formatRupees(valueInMinorUnits) {
    const amount = Math.round(Number(valueInMinorUnits || 0) / 100);
    return `₹${amount.toLocaleString('en-IN')}`;
  }
}

if (!customElements.get('maati-pdp')) {
  customElements.define('maati-pdp', MaatiPdp);
}
